import PropTypes from 'prop-types';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import { BarChart } from 'echarts/charts';
import { GridComponent, TooltipComponent } from 'echarts/components';
import * as echarts from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { Card } from 'react-bootstrap';
import FalconCardHeader from '@/components/common/FalconCardHeader';
import HelpTooltip from '@/app/common/components/HelpTooltip';
import { getColor } from '@/helpers/utils';
import numberHumanizer from '@/helpers/numberHumanizer';

echarts.use([BarChart, GridComponent, TooltipComponent, CanvasRenderer]);

const SCORE_BUCKETS = [
  '0-10',
  '11-20',
  '21-30',
  '31-40',
  '41-50',
  '51-60',
  '61-70',
  '71-80',
  '81-90',
  '91-100'
];

const normalizeData = data =>
  SCORE_BUCKETS.map(name => ({
    name,
    value: data?.find(item => item.name === name)?.value ?? 0
  }));

const formatAverage = average =>
  average == null
    ? '—'
    : Number(average).toLocaleString('fa-IR', {
        maximumFractionDigits: 1
      });

const getOption = rows => ({
  textStyle: {
    fontFamily: 'dana'
  },
  tooltip: {
    trigger: 'axis',
    axisPointer: {
      type: 'shadow'
    },
    padding: [7, 10],
    backgroundColor: getColor('gray-100'),
    borderColor: getColor('gray-300'),
    borderWidth: 1,
    transitionDuration: 0,
    textStyle: {
      color: getColor('gray-1100')
    },
    formatter: params => {
      const item = params[0];
      return `<div><strong>${item.name}:</strong> ${numberHumanizer(
        item.value
      )} دامنه</div>`;
    }
  },
  xAxis: {
    type: 'value',
    min: 0,
    axisLabel: {
      color: getColor('gray-400'),
      margin: 10
    },
    splitLine: {
      show: true,
      lineStyle: {
        color: getColor('gray-200')
      }
    },
    axisTick: {
      show: false
    },
    axisLine: {
      show: false
    }
  },
  yAxis: {
    type: 'category',
    inverse: true,
    data: rows.map(row => row.name),
    axisLabel: {
      color: getColor('gray-400'),
      margin: 12
    },
    axisTick: {
      show: false
    },
    axisLine: {
      show: false
    }
  },
  series: [
    {
      type: 'bar',
      data: rows.map(row => row.value),
      barMaxWidth: 22,
      itemStyle: {
        color: getColor('primary'),
        borderRadius: [0, 3, 3, 0]
      }
    }
  ],
  grid: {
    containLabel: true,
    right: '4%',
    left: '3%',
    bottom: '3%',
    top: '3%'
  }
});

const ReferralDomainScoreChart = ({ title, data }: Record<string, any>) => {
  const rows = normalizeData(data?.distribution);

  return (
    <Card className="h-100">
      <FalconCardHeader
        title={
          <div className="d-flex align-items-center gap-2">
            <span>{title}</span>
            <HelpTooltip html="واحد شمارش، دامنه‌های ارجاع‌دهنده است. دامنه‌هایی که برای این معیار امتیاز ندارند در بازه‌ها شمرده نمی‌شوند." />
          </div>
        }
        titleTag="h6"
        className="py-2"
        light
        endEl={
          <div
            className="d-flex align-items-center gap-1 px-2 text-nowrap"
            dir="rtl"
          >
            <span className="fs--2 text-600">میانگین:</span>
            <span className="fw-bold text-primary">
              {formatAverage(data?.average)}
            </span>
          </div>
        }
      />
      <Card.Body className="py-2">
        <ReactEChartsCore
          echarts={echarts}
          option={getOption(rows)}
          style={{ height: '20rem' }}
          notMerge
        />
      </Card.Body>
    </Card>
  );
};

ReferralDomainScoreChart.propTypes = {
  title: PropTypes.string.isRequired,
  data: PropTypes.shape({
    average: PropTypes.number,
    distribution: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        value: PropTypes.number
      })
    )
  })
};

export default ReferralDomainScoreChart;
